import MoreVertIcon from '@mui/icons-material/MoreVert';
import { IconButton } from '@mui/material';
import { useMemo, useState } from 'react';
import Drawer from '../../../components/Drawer';
import { Player } from '../../../components/PlayerCircle/PlayerCircle';
import {
  ExtendedTeam,
  TeamColor,
} from '../../../components/PlayerCircle/TeamCircle';
import JudgeCrossPage, {
  JudgeCrossState,
} from '../../JudgeCrossPage/JudgeCrossPage';
import { getTeamPoints, getTeamWarnings } from '../helpers';
import { ConfirmSwapPlayersModal } from './ConfirmSwapPlayersModal';
import { JudgeCrossMenu } from './JudgeCrossMenu';
import { SwapPlayersModal } from './SwapPlayersModal';

export interface JudgeCrossDrawerProps {
  open: boolean;
  onClose: () => void;
  storedState: JudgeCrossState;
}

export default function JudgeCrossDrawer({
  open,
  onClose,
  storedState,
}: JudgeCrossDrawerProps) {
  const [swapPlayersModalOpen, setSwapPlayersModalOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      actionButton={
        <>
          <IconButton
            onClick={event => setAnchorEl(event.currentTarget)}
            size="large"
            aria-haspopup="true"
            aria-controls={isMenuOpen ? 'judge-cross-menu' : undefined}
            aria-expanded={isMenuOpen ? 'true' : undefined}
          >
            <MoreVertIcon />
          </IconButton>

          <JudgeCrossMenu
            anchorEl={anchorEl}
            open={isMenuOpen}
            onClose={() => setAnchorEl(null)}
            setSwapPlayersModalOpen={setSwapPlayersModalOpen}
          />
        </>
      }
    >
      <DrawerContent
        storedState={storedState}
        swapPlayersModalOpen={swapPlayersModalOpen}
        setSwapPlayersModalOpen={setSwapPlayersModalOpen}
      />
    </Drawer>
  );
}

export interface TestProps {
  storedState: JudgeCrossState;
  swapPlayersModalOpen: boolean;
  setSwapPlayersModalOpen: (open: boolean) => void;
}

function DrawerContent({
  storedState,
  swapPlayersModalOpen,
  setSwapPlayersModalOpen,
}: TestProps) {
  const [currentState, setCurrentState] =
    useState<JudgeCrossState>(storedState);
  localStorage.setItem('tools.judgeCross', JSON.stringify(currentState));

  const { pointSystem, maxPoints, teams } = currentState;
  const isLivesPointSystem = pointSystem === 'lives';

  const extendedTeams: readonly ExtendedTeam[] = teams.map(team => {
    const { players, index } = team;
    const otherTeams = teams.filter(t => t.id !== team.id);
    const halfTimePoints = isLivesPointSystem
      ? Math.floor(maxPoints / 2)
      : Math.ceil(maxPoints / 2);

    const teamPoints = getTeamPoints(players, isLivesPointSystem, maxPoints);
    const teamWarnings = getTeamWarnings(players);
    const teamColor = index === 0 ? 'blue' : index === 1 ? 'orange' : undefined;
    const shouldTeamSwap =
      teamPoints === halfTimePoints &&
      otherTeams.some(t =>
        isLivesPointSystem
          ? getTeamPoints(t.players, isLivesPointSystem, maxPoints) >
            halfTimePoints
          : getTeamPoints(t.players, isLivesPointSystem, maxPoints) <
            halfTimePoints,
      );

    return {
      ...team,
      color: teamColor as TeamColor,
      points: teamPoints,
      warnings: teamWarnings,
      shouldSwap: shouldTeamSwap,
    };
  });

  const players: readonly Player[] = useMemo(() => {
    return teams
      .flatMap(team => team.players)
      .toSorted((a, b) => a.index - b.index);
  }, [teams]);

  const teamToSwap = extendedTeams.find(team => team.shouldSwap);

  return (
    <>
      <JudgeCrossPage
        currentState={currentState}
        setCurrentState={setCurrentState}
      />
      <SwapPlayersModal
        open={swapPlayersModalOpen}
        onClose={() => setSwapPlayersModalOpen(false)}
        players={players}
        currentState={currentState}
        setCurrentState={setCurrentState}
      />
      {teamToSwap !== undefined && (
        <ConfirmSwapPlayersModal
          teamToSwap={teamToSwap}
          currentState={currentState}
          setCurrentState={setCurrentState}
        />
      )}
    </>
  );
}
